import React, { useState } from 'react';
import {
  Loader2,
  AlertTriangle,
  Check,
  Trash2,
  ExternalLink,
  KeyRound,
  ShieldCheck,
} from 'lucide-react';
import { api, type Profile } from '../lib/api';
import { Field, INPUT } from './ProfileFields';

interface Props {
  profile: Profile;
  onProfileChange: (p: Profile) => void;
}

const URL_CLAVES = 'https://aistudio.google.com/apikey';

/**
 * Clave de Google AI Studio de cada persona.
 *
 * La generación con IA la paga quien la usa, con su propia clave. Así el
 * servidor no sostiene una factura común que cualquiera podría disparar
 * generando cuestionarios en bucle, y nadie depende de que el dueño del sitio
 * mantenga saldo.
 *
 * La guía va aquí dentro y no en un enlace a otra página: quien llega a esta
 * pestaña es porque quiere usar la IA ahora, y mandarle a buscar la
 * documentación de Google es donde se pierde la mitad de la gente.
 */
export const AiKeyPanel: React.FC<Props> = ({ profile, onProfileChange }) => {
  const [clave, setClave] = useState('');
  const [estado, setEstado] = useState<
    { tipo: 'quieto' } | { tipo: 'probando' } | { tipo: 'guardado' } | { tipo: 'error'; mensaje: string }
  >({ tipo: 'quieto' });

  const tieneClave = Boolean(profile.aiKeyPreview);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEstado({ tipo: 'probando' });
    try {
      const data = await api.put<{ profile: Profile }>('/profile/ai-key', { key: clave.trim() });
      onProfileChange(data.profile);
      setClave('');
      setEstado({ tipo: 'guardado' });
    } catch (err) {
      setEstado({
        tipo: 'error',
        mensaje: err instanceof Error ? err.message : 'No se pudo guardar la clave.',
      });
    }
  };

  const quitar = async () => {
    setEstado({ tipo: 'probando' });
    try {
      const data = await api.delete<{ profile: Profile }>('/profile/ai-key');
      onProfileChange(data.profile);
      setEstado({ tipo: 'quieto' });
    } catch (err) {
      setEstado({
        tipo: 'error',
        mensaje: err instanceof Error ? err.message : 'No se pudo quitar la clave.',
      });
    }
  };

  return (
    <div className="px-6 py-5 space-y-5">
      {/* Estado actual */}
      {tieneClave ? (
        <div className="flex items-center gap-3 rounded-lg border border-success-400/30 bg-success-400/10 px-3.5 py-3">
          <ShieldCheck className="w-5 h-5 text-success-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[0.8125rem] font-medium text-ink-100">
              Generación con IA activada
            </p>
            <p className="text-[0.75rem] text-ink-400 font-mono">{profile.aiKeyPreview}</p>
          </div>
          <button
            type="button"
            onClick={() => void quitar()}
            disabled={estado.tipo === 'probando'}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[0.8125rem] font-medium
                       text-ink-400 hover:text-danger-400 hover:bg-danger-400/10 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Quitar
          </button>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-lg border border-ink-700 bg-ink-850 px-3.5 py-3">
          <KeyRound className="w-5 h-5 text-ink-500 shrink-0 mt-0.5" />
          <p className="text-[0.8125rem] text-ink-300 leading-relaxed">
            Para generar cuestionarios con IA necesitas una clave tuya de Google AI Studio. Es
            gratis, se saca en dos minutos y tú controlas su consumo. Crear cuestionarios a mano
            no requiere nada de esto.
          </p>
        </div>
      )}

      {/* Guía */}
      <section className="space-y-3">
        <h3 className="text-[0.8125rem] font-semibold text-ink-200">Cómo conseguir tu clave</h3>

        <ol className="space-y-2.5">
          <Paso numero={1}>
            Abre{' '}
            <a
              href={URL_CLAVES}
              target="_blank"
              rel="noreferrer noopener"
              className="text-sky-400 hover:text-sky-300 font-medium inline-flex items-center gap-1"
            >
              aistudio.google.com/apikey
              <ExternalLink className="w-3 h-3" />
            </a>{' '}
            e inicia sesión con tu cuenta de Google.
          </Paso>
          <Paso numero={2}>
            Pulsa <Boton>Crear clave de API</Boton>. Si te pide un proyecto, deja el que te ofrece
            o crea uno nuevo: sirve cualquiera.
          </Paso>
          <Paso numero={3}>
            Copia la clave que aparece. Empieza por <code className="text-ink-200">AIza</code> y
            tiene unos 39 caracteres.
          </Paso>
          <Paso numero={4}>Pégala aquí abajo y pulsa Guardar. Comprobamos que funciona.</Paso>
        </ol>

        <p className="text-[0.6875rem] leading-relaxed text-ink-500">
          Tu clave se guarda cifrada y se usa solo para generar tus cuestionarios. Ni tú vuelves
          a verla entera desde aquí; únicamente la administración de Open Dahu puede consultarla
          junto con su consumo.
        </p>

        <p className="text-[0.6875rem] leading-relaxed text-ink-500">
          Google ofrece un nivel gratuito con un límite diario de peticiones, de sobra para crear
          cuestionarios de vez en cuando. Si lo superas, la generación falla con un aviso y vuelve
          a funcionar al día siguiente; no se te cobra nada sin que actives la facturación en tu
          cuenta de Google.
        </p>
      </section>

      {/* Formulario */}
      <form onSubmit={guardar} className="space-y-3">
        <Field
          label={tieneClave ? 'Reemplazar por otra clave' : 'Tu clave de API'}
          hint="Se guarda cifrada y no vuelve a mostrártela entera: solo verás los últimos caracteres. Puedes cambiarla o borrarla cuando quieras."
        >
          <input
            value={clave}
            onChange={(e) => {
              setClave(e.target.value);
              setEstado({ tipo: 'quieto' });
            }}
            // `password` y no `text`: esto es una credencial, y la gente rellena
            // esto con la pantalla compartida en clase más a menudo de lo que
            // parece.
            type="password"
            autoComplete="off"
            spellCheck={false}
            placeholder="AIza..."
            className={`${INPUT} font-mono`}
          />
        </Field>

        {estado.tipo === 'error' && (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-danger-400/30 bg-danger-400/10 px-3 py-2.5 text-danger-400 text-[0.8125rem]"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
            {estado.mensaje}
          </p>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={clave.trim().length < 20 || estado.tipo === 'probando'}
            className="flex items-center justify-center gap-2 px-5 h-11 rounded-lg font-semibold text-sm
                       bg-ember-500 hover:bg-ember-400 text-on-accent
                       disabled:bg-ink-800 disabled:text-ink-500 disabled:cursor-not-allowed transition-colors"
          >
            {estado.tipo === 'probando' && <Loader2 className="w-4 h-4 animate-spin" />}
            {estado.tipo === 'probando' ? 'Comprobando con Google…' : 'Guardar clave'}
          </button>

          {estado.tipo === 'guardado' && (
            <span className="flex items-center gap-1.5 text-success-400 text-[0.8125rem]">
              <Check className="w-4 h-4" />
              Clave verificada y guardada
            </span>
          )}
        </div>
      </form>
    </div>
  );
};

const Paso: React.FC<{ numero: number; children: React.ReactNode }> = ({ numero, children }) => (
  <li className="flex gap-3">
    <span
      aria-hidden="true"
      className="shrink-0 w-5 h-5 rounded-full bg-ink-800 border border-ink-700 text-ink-300
                 text-[0.6875rem] font-semibold flex items-center justify-center mt-px"
    >
      {numero}
    </span>
    <span className="text-[0.8125rem] leading-relaxed text-ink-300">{children}</span>
  </li>
);

/** Rótulo de un botón de otra aplicación, tal y como se lee en su pantalla. */
const Boton: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="px-1.5 py-0.5 rounded border border-ink-700 bg-ink-850 text-ink-200 text-[0.75rem] font-medium">
    {children}
  </span>
);
